import { useEffect, useRef, useState } from "react";
import { Filter as FilterIcon } from "lucide-react";

/**
 * Reusable filter popover used across list pages.
 *
 * Props:
 *  - groups: [{ key, title, options: [{ value, label }] }]
 *  - value:  { [groupKey]: string[] }   // active selected values per group
 *  - onChange: (next) => void
 *  - matchCount: number                  // shown in the footer
 *  - testid?: string                     // applied to wrapper + button
 */
export default function FilterPopover({ groups, value, onChange, matchCount, testid = "filter" }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (open && anchorRef.current && !anchorRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const totalActive = Object.values(value || {}).reduce(
    (a, arr) => a + (arr?.length || 0), 0
  );

  const toggle = (gKey, v) => {
    const cur = value?.[gKey] || [];
    const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
    onChange({ ...value, [gKey]: next });
  };

  const clearAll = () => {
    const cleared = {};
    groups.forEach((g) => { cleared[g.key] = []; });
    onChange(cleared);
  };

  return (
    <div className="filter-anchor" ref={anchorRef}>
      <button
        type="button"
        className={`ghost-pill ${totalActive > 0 ? "active" : ""}`}
        data-testid={`${testid}-btn`}
        onClick={() => setOpen((s) => !s)}
      >
        <FilterIcon /> Filter{totalActive > 0 ? ` · ${totalActive}` : ""}
      </button>
      {open && (
        <div className="filter-pop" data-testid={`${testid}-popover`} onMouseDown={(e) => e.stopPropagation()}>
          {groups.map((g) => (
            <div key={g.key} className="filter-group">
              <div className="filter-group-title">{g.title}</div>
              <div className="filter-options">
                {g.options.length === 0 && (
                  <span className="filter-count" style={{ fontStyle: "italic" }}>none available</span>
                )}
                {g.options.map((opt) => {
                  const active = (value?.[g.key] || []).includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`filter-chip ${active ? "active" : ""}`}
                      data-testid={`${testid}-${g.key}-${opt.value}`}
                      onClick={() => toggle(g.key, opt.value)}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="filter-footer">
            <button
              type="button"
              className="filter-clear"
              data-testid={`${testid}-clear`}
              onClick={clearAll}
            >
              Clear filters
            </button>
            <span className="filter-count">{matchCount} match{matchCount === 1 ? "" : "es"}</span>
          </div>
        </div>
      )}
    </div>
  );
}
